import type { RawDealItemForValidation } from '@/lib/workers/validation.worker';
import { convertDateFields } from '@/lib/apollo-client';
import { getDealsWebsocketBaseUrl } from '@/lib/config';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import {
  mergeStreamedItem,
  normalize,
  toRawValidationItem,
} from '@/lib/streaming/normalize-deal-items';
import {
  type DealItemsBatch,
  DealItemsStreamError,
  streamDealItems,
} from '@/lib/streaming/stream-deal-items';
import {
  markBackendConfirmed,
  resetValidationWorker,
  validate,
} from '@/lib/validation/validation-service';
import { dealItemDescriptionsByActiveVersionIdState } from '@/states/deal-item-descriptions.state';
import { pendingOptimisticUpdatesState } from '@/states/pending-optimistic-updates.state';
import {
  backendValidatedProductIdsAtom,
  dealItemsLoadingState,
  dealItemsStreamingState,
  localValidationErrorsAtom,
  productsMapState,
} from '@/states/products.state';
import { storeAtom } from '@/states/store.state';
import { getDefaultStore } from 'jotai';

const store = getDefaultStore();

const STREAM_MAX_RETRIES = 2;
const STREAM_RETRY_DELAY_MS = 300;

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export type DealItemsStreamResult = 'success' | 'error' | 'aborted';

type ActiveStream = {
  dealId: string;
  controller: AbortController;
  promise: Promise<DealItemsStreamResult>;
  queuedRefresh: Promise<DealItemsStreamResult> | null;
};

let active: ActiveStream | null = null;
let completedDealId: string | null = null;

/**
 * Items retirés par un signal de suppression et qu'aucune lecture ne doit réinjecter.
 *
 * Une lecture partie avant la suppression peut revenir après le signal avec l'item
 * encore vivant — ciblée, ou complète et en cours de bufferisation. Sans cette garde,
 * la ligne réapparaîtrait jusqu'au prochain rechargement. Vidé quand un chargement
 * complet aboutit : son résultat fait foi, et sa purge finale retire de toute façon
 * ce qu'il n'a pas relu.
 */
const removedItemIds = new Set<string>();

type EnsureArgs = {
  dealId: string;
  isRefresh?: boolean;
  getToken: () => Promise<string>;
};

const isNonRetryableStreamError = (caught: unknown): boolean =>
  caught instanceof DealItemsStreamError &&
  caught.status !== undefined &&
  caught.status >= 400 &&
  caught.status < 500;

export function ensureDealItemsStream({
  dealId,
  isRefresh = false,
  getToken,
}: EnsureArgs): Promise<DealItemsStreamResult> {
  if (!dealId) return Promise.resolve('aborted');

  if (active && active.dealId === dealId) {
    if (!isRefresh) return active.promise;
    if (!active.queuedRefresh) {
      const { controller } = active;
      active.queuedRefresh = active.promise.then((result) => {
        if (controller.signal.aborted) return 'aborted';
        if (result === 'aborted') return result;
        return ensureDealItemsStream({ dealId, isRefresh: true, getToken });
      });
    }
    return active.queuedRefresh;
  }

  if (!isRefresh && completedDealId === dealId) {
    isRefresh = true;
  }

  if (active) {
    active.controller.abort();
    active = null;
  }

  const controller = new AbortController();
  const promise = runStream({ dealId, isRefresh, getToken, controller });
  active = { dealId, controller, promise, queuedRefresh: null };
  return promise;
}

/**
 * Le deal dont la map porte les items : celui en cours de chargement, ou le dernier
 * chargé. Toute lecture hors du chargement principal doit s'y confronter avant d'écrire
 * dans la map, qui est globale et ne distingue pas les deals.
 */
function isDealDisplayed(dealId: string): boolean {
  return active?.dealId === dealId || completedDealId === dealId;
}

/**
 * Fusionne des items normalisés dans la map, en préservant les éditions optimistes
 * et en ignorant les items retirés entre-temps par un signal de suppression.
 *
 * Si aucun item n'a changé (même référence après `mergeStreamedItem`), la map
 * d'origine est conservée : ses abonnés — table, validation, surlignage — ne
 * sont pas notifiés. C'est le cas courant d'une relecture complète après une
 * reconnexion SSE.
 */
function applyItemsToProductsMap(
  normalizedItems: DealItemWithVersions[],
  unchangedSince?: ReadonlyMap<string, DealItemWithVersions>
): void {
  const pendingIds = store.get(pendingOptimisticUpdatesState);
  store.set(productsMapState, (prev) => {
    let next: Map<string, DealItemWithVersions> | null = null;
    for (const n of normalizedItems) {
      if (removedItemIds.has(n.id)) continue;
      const current = (next ?? prev).get(n.id);
      if (
        unchangedSince &&
        current !== unchangedSince.get(n.id) &&
        !pendingIds.has(n.id)
      )
        continue;
      const merged = mergeStreamedItem(n, current, pendingIds);
      if (merged === current) continue;
      next ??= new Map(prev);
      next.set(n.id, merged);
    }
    return next ?? prev;
  });
}

/**
 * Retire **ces items** de la table, sans lecture.
 *
 * C'est la réponse au signal de suppression : le serveur nomme les items partis, et
 * une lecture ciblée ne les renverrait de toute façon jamais. Le retrait suffit à
 * faire disparaître leurs erreurs de validation, dérivées de la map.
 *
 * Les éditions optimistes en attente sur ces items sont abandonnées : il n'y a plus
 * rien à confirmer.
 *
 * Ignoré si la map porte un autre deal — le signal est arrivé après une navigation.
 */
export function removeDealItems(dealId: string, itemIds: string[]): void {
  if (!dealId || itemIds.length === 0 || !isDealDisplayed(dealId)) return;

  for (const id of itemIds) removedItemIds.add(id);

  store.set(productsMapState, (prev) => {
    if (!itemIds.some((id) => prev.has(id))) return prev;
    const next = new Map(prev);
    for (const id of itemIds) next.delete(id);
    return next;
  });
  store.set(pendingOptimisticUpdatesState, (prev) => {
    if (!itemIds.some((id) => prev.has(id))) return prev;
    const next = new Set(prev);
    for (const id of itemIds) next.delete(id);
    return next;
  });
}

type RefreshByIdsArgs = {
  dealId: string;
  itemIds: string[];
  getToken: () => Promise<string>;
};

/**
 * Relit **ces items seulement** et les fusionne dans la map.
 *
 * Pensé pour l'onglet qui vient d'écrire : il connaît les items qu'il a modifiés, et
 * n'a pas besoin des mégaoctets du deal entier pour voir ce que le serveur en a fait
 * (marge et prix recalculés, erreurs de validation). Sur une grosse offre c'est la
 * différence entre une ligne juste tout de suite et une ligne juste dans dix secondes.
 *
 * Volontairement **hors** du cycle de vie du chargement complet, dont il ne partage ni
 * l'annulation ni l'état :
 * - aucune purge des items absents de la réponse — elle ne contient que ce qui a été
 *   demandé, donc élaguer viderait la table ;
 * - aucun `dealItemsLoadingState` ni `dealItemsStreamingState` — rien n'est en attente
 *   à l'écran, les lignes concernées sont déjà là ;
 * - aucune revalidation locale — le worker valide le deal entier (unicité GTIN, SKU),
 *   lui passer une poignée d'items lui ferait croire que le deal s'y résume. Les
 *   erreurs qui comptent pour ces lignes arrivent avec elles et `markBackendConfirmed`
 *   les désigne comme faisant foi.
 *
 * Ne remplace pas la relecture complète déclenchée par le signal : c'est une avance. Les
 * deux écrivent dans la même map sans se coordonner, et c'est sans conséquence — le
 * serveur ne publie qu'après avoir commité, donc la relecture complète voit au moins ce
 * que celle-ci a vu, et la dernière arrivée gagne à bon droit.
 *
 * Un échec est silencieux : le rattrapage viendra.
 */
export async function refreshDealItemsByIds({
  dealId,
  itemIds,
  getToken,
}: RefreshByIdsArgs): Promise<DealItemsStreamResult> {
  if (!dealId || itemIds.length === 0) return 'aborted';

  const controller = new AbortController();
  const streamedIds: string[] = [];

  // La map est globale et repart de zéro au chargement d'un autre deal : une réponse
  // arrivant après une navigation y injecterait les lignes du deal précédent.
  //
  // Aucune garde en revanche contre le chargement complet qui suit le signal, alors qu'il
  // réécrit la map d'un bloc : il ne peut pas être plus périmé que cette lecture. Le
  // serveur ne publie `deal_items_updated` qu'après avoir commité (phase `emitEvents`,
  // après `persist`), donc le chargement qu'il déclenche lit un état postérieur à
  // l'écriture. Se retirer dès qu'il démarre — ce que faisait une version précédente —
  // revenait à ne jamais rien appliquer, puisqu'il démarre à chaque signal.
  const isStillOurs = (): boolean => isDealDisplayed(dealId);

  const onBatch = (batch: DealItemsBatch): void => {
    if (!isStillOurs()) {
      // ⚠️ TEMPORAIRE (debug) — à retirer avant merge.
      console.debug('[refreshDealItemsByIds] batch ignoré (deal changé)', {
        dealId,
        received: batch.items.length,
      });
      controller.abort();
      return;
    }
    convertDateFields(batch);
    for (const item of batch.items) streamedIds.push(item.id);
    applyItemsToProductsMap(normalize(batch.items));
    // ⚠️ TEMPORAIRE (debug) — à retirer avant merge.
    console.debug('[refreshDealItemsByIds] batch fusionné', {
      dealId,
      asked: itemIds,
      received: batch.items.map((item) => item.id),
      isLastBatch: batch.isLastBatch,
    });
  };

  try {
    // ⚠️ TEMPORAIRE (debug) — à retirer avant merge.
    console.debug('[refreshDealItemsByIds] départ', { dealId, itemIds });
    const token = await getToken();
    if (!isStillOurs()) return 'aborted';
    await streamDealItems({
      baseUrl: getDealsWebsocketBaseUrl(),
      dealId,
      token,
      signal: controller.signal,
      onBatch,
      itemIds,
    });
    if (controller.signal.aborted || !isStillOurs()) return 'aborted';
    markBackendConfirmed(streamedIds);
    // ⚠️ TEMPORAIRE (debug) — à retirer avant merge.
    console.debug('[refreshDealItemsByIds] terminé', { dealId, streamedIds });
    return 'success';
  } catch (error) {
    // ⚠️ TEMPORAIRE (debug) — à retirer avant merge.
    console.debug('[refreshDealItemsByIds] échec', {
      dealId,
      aborted: controller.signal.aborted,
      error,
    });
    return controller.signal.aborted ? 'aborted' : 'error';
  }
}

/** Aborts the in-flight stream for this deal (useEffect cleanup on unmount). */
export function stopDealItemsStream(dealId: string): void {
  if (active && active.dealId === dealId) {
    active.controller.abort();
    active = null;
  }
}

async function runStream({
  dealId,
  isRefresh,
  getToken,
  controller,
}: Required<Omit<EnsureArgs, 'isRefresh'>> & {
  isRefresh: boolean;
  controller: AbortController;
}): Promise<DealItemsStreamResult> {
  const itemsAtStart = store.get(productsMapState);
  const existingIdsAtStart = isRefresh
    ? new Set(itemsAtStart.keys())
    : new Set<string>();
  const buffered = isRefresh;

  store.set(dealItemsStreamingState, existingIdsAtStart.size === 0);

  if (!isRefresh) {
    completedDealId = null;
    removedItemIds.clear();
    resetValidationWorker();
    store.set(productsMapState, new Map());
    store.set(dealItemDescriptionsByActiveVersionIdState, null);
    store.set(backendValidatedProductIdsAtom, new Set());
    store.set(localValidationErrorsAtom, null);
    store.set(dealItemsLoadingState, {
      initialLoading: true,
      error: undefined,
    });
  }

  let firstBatchReceived = false;
  let validationItems: RawDealItemForValidation[] = [];
  let streamedIds = new Set<string>();
  let bufferedItems: DealItemWithVersions[] = [];
  const idsFromFailedAttempts = new Set<string>();

  const attemptStream = async (): Promise<void> => {
    for (const id of streamedIds) idsFromFailedAttempts.add(id);
    validationItems = [];
    streamedIds = new Set<string>();
    bufferedItems = [];

    const onBatch = (batch: DealItemsBatch): void => {
      if (controller.signal.aborted) return;
      convertDateFields(batch);
      for (const item of batch.items) streamedIds.add(item.id);
      const dealStore = store.get(storeAtom);
      validationItems.push(
        ...batch.items.map((item) => toRawValidationItem(item, dealStore))
      );

      const normalizedItems = normalize(batch.items);
      if (buffered) {
        bufferedItems.push(...normalizedItems);
        return;
      }

      applyItemsToProductsMap(normalizedItems);
      markBackendConfirmed(batch.items.map((item) => item.id));
      validate(validationItems);

      if (!firstBatchReceived) {
        firstBatchReceived = true;
        store.set(dealItemsLoadingState, {
          initialLoading: false,
          error: undefined,
        });
      }
    };

    const token = await getToken();
    if (controller.signal.aborted) return;

    await streamDealItems({
      baseUrl: getDealsWebsocketBaseUrl(),
      dealId,
      token,
      signal: controller.signal,
      onBatch,
    });
  };

  try {
    for (let attempt = 0; ; attempt++) {
      try {
        await attemptStream();
        break;
      } catch (caught) {
        if (
          controller.signal.aborted ||
          attempt >= STREAM_MAX_RETRIES ||
          isNonRetryableStreamError(caught)
        ) {
          throw caught;
        }
        await delay(STREAM_RETRY_DELAY_MS * (attempt + 1));
        if (controller.signal.aborted) return 'aborted';
      }
    }

    if (controller.signal.aborted) return 'aborted';

    if (buffered) {
      applyItemsToProductsMap(bufferedItems, itemsAtStart);
      markBackendConfirmed(Array.from(streamedIds));
    }

    if (isRefresh) {
      store.set(productsMapState, (prev) => {
        const next = new Map<string, DealItemWithVersions>();
        for (const [id, item] of prev) {
          if (streamedIds.has(id) || !existingIdsAtStart.has(id)) {
            next.set(id, item);
          }
        }
        // Rien de purgé : garder la référence pour ne notifier personne.
        return next.size === prev.size ? prev : next;
      });
    } else if (idsFromFailedAttempts.size > 0) {
      store.set(productsMapState, (prev) => {
        const next = new Map(prev);
        for (const id of idsFromFailedAttempts) {
          if (!streamedIds.has(id)) next.delete(id);
        }
        return next;
      });
    }

    validate(validationItems);
    store.set(dealItemsLoadingState, {
      initialLoading: false,
      error: undefined,
    });
    store.set(dealItemsStreamingState, false);
    completedDealId = dealId;
    removedItemIds.clear();
    return 'success';
  } catch (caught) {
    if (controller.signal.aborted) return 'aborted';
    store.set(dealItemsStreamingState, false);
    if (!isRefresh || store.get(productsMapState).size === 0) {
      const error =
        caught instanceof Error ? caught : new Error(String(caught));
      store.set(dealItemsLoadingState, { initialLoading: false, error });
    }
    return 'error';
  } finally {
    const ownsGlobalState = active === null || active.controller === controller;
    if (active && active.controller === controller) {
      active = null;
    }
    if (controller.signal.aborted && ownsGlobalState) {
      store.set(dealItemsStreamingState, false);
    }
  }
}

export function __resetDealItemsStreamControllerForTests(): void {
  if (active) active.controller.abort();
  active = null;
  completedDealId = null;
  removedItemIds.clear();
}
